import React, { useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as NewPagesIcon,
  DarkModeOutlined as DarkIcon,
  Contrast as OledIcon,
  LightModeOutlined as LightIcon,
  SettingsBrightnessOutlined as SystemIcon,
  AccountCircle as ProfileIcon,
  ContactSupport as SupportIcon,
  Logout as LogoutIcon,
  MenuBook as GuideIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { SUPPORT_CONFIG } from '../config/local.config';

const GUIDES = [
  { text: 'User guide', url: 'https://tegita0-my.sharepoint.com/:p:/g/personal/massimiliano_patrizi_avvale_com/cQoZdwznUyv1QqRiISTxTM69EgUCqfajq1JLdVXGgkX1rUKZdA' },
  { text: 'Manager guide', url: 'https://tegita0-my.sharepoint.com/:f:/r/personal/massimiliano_patrizi_avvale_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Fmassimiliano%5Fpatrizi%5Favvale%5Fcom%2FDocuments%2FRUNTime%2FDocumentazione%2FManager&share=cgo4R%5FxZowyeR642gsLptnfiEgUCuPDsSBg7GGmTBDyu6mGEMQ' },
];

// Menu utente: pannello con angoli morbidi e ombra leggera, staccato dal pulsante; voci a pillola
const menuPaperSx = (variant) => ({
  minWidth: 260,
  borderRadius: '16px',
  // staccato di ~10px dal bordo della sidebar
  ...(variant === 'avatar' ? { mt: 1 } : { ml: '22px' }),
  boxShadow: '0 18px 40px -12px rgba(15,23,18,0.28), 0 4px 12px rgba(15,23,18,0.10)',
  border: '1px solid',
  borderColor: 'divider',
});
const menuListSx = {
  px: 0.75,
  py: 0.75,
  '& .MuiMenuItem-root': { borderRadius: '10px', minHeight: 38, px: 1.25, my: 0.125 },
  '& .MuiDivider-root': { mx: 0.75, my: '6px !important' },
};

/** Iniziali da nome e cognome (o dalla prima lettera disponibile). */
export function userInitials(user) {
  const name = (user?.name || user?.username || '').trim();
  const surname = (user?.surname || '').trim();
  if (name && surname) return `${name[0]}${surname[0]}`.toUpperCase();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return (name[0] || surname[0] || '?').toUpperCase();
}

/**
 * Utente corrente con il suo menu (profilo, guide, supporto, pagine V2, logout).
 * variant="sidebar": blocco in fondo al menu laterale (compatto se il menu è chiuso).
 * variant="avatar": solo avatar, per la barra sottile su mobile.
 * L'interruttore delle pagine V2 compare solo se viene passato onToggleV2Pages.
 */
export default function UserMenu({
  user,
  email,
  roleLabel,
  notInDatabase,
  collapsed,
  variant = 'sidebar',
  colors,
  onNavigate,
  v2Pages,
  onToggleV2Pages,
  themePref,
  onThemePrefChange,
  rounded,
}) {
  const [anchor, setAnchor] = useState(null);
  const displayName = user ? [user.name, user.surname].filter(Boolean).join(' ') || user.username || email : email;
  const close = () => setAnchor(null);
  const go = (path) => {
    close();
    onNavigate(path);
  };

  const avatar = (
    <Avatar sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 600, bgcolor: '#5ab031', color: '#ffffff' }}>
      {userInitials(user || { name: email })}
    </Avatar>
  );

  let trigger;
  if (variant === 'avatar') {
    trigger = (
      <ButtonBase onClick={(e) => setAnchor(e.currentTarget)} aria-label="Open user menu" sx={{ borderRadius: '50%' }}>
        {avatar}
      </ButtonBase>
    );
  } else if (collapsed) {
    trigger = (
      <Tooltip title={displayName || 'Account'} placement="right" arrow>
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Open user menu"
          sx={{ width: '100%', py: 1.25, justifyContent: 'center', borderRadius: rounded ? '14px' : 0, '&:hover': { bgcolor: colors.hover } }}
        >
          {avatar}
        </ButtonBase>
      </Tooltip>
    );
  } else {
    trigger = (
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="Open user menu"
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          textAlign: 'left',
          borderRadius: rounded ? '14px' : 0,
          '&:hover': { bgcolor: colors.hover },
        }}
      >
        {avatar}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: colors.text, lineHeight: 1.3 }}>
            {displayName}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: notInDatabase ? '#ff8a80' : colors.muted, lineHeight: 1.3 }}>
            {notInDatabase ? 'Not found in database' : roleLabel}
          </Typography>
        </Box>
        <MoreIcon sx={{ fontSize: 18, color: colors.muted }} />
      </ButtonBase>
    );
  }

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        anchorOrigin={variant === 'avatar' ? { vertical: 'bottom', horizontal: 'right' } : { vertical: 'top', horizontal: 'right' }}
        transformOrigin={variant === 'avatar' ? { vertical: 'top', horizontal: 'right' } : { vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: menuPaperSx(variant) }}
        MenuListProps={{ sx: menuListSx }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, pt: 1, pb: 1.25 }}>
          {avatar}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
              {displayName}
            </Typography>
            {email && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                {email}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: notInDatabase ? 'error.main' : 'text.secondary' }}>
              {notInDatabase ? 'Not found in database' : roleLabel}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={() => go('/profile')}>
          <ListItemIcon>
            <ProfileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        {GUIDES.map((g) => (
          <MenuItem key={g.url} component="a" href={g.url} target="_blank" rel="noopener noreferrer" onClick={close}>
            <ListItemIcon>
              <GuideIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{g.text}</ListItemText>
          </MenuItem>
        ))}
        <MenuItem
          component="a"
          href={`mailto:${SUPPORT_CONFIG.email}?subject=Support Request - RUNtime App - [Brief description of the issue]`}
          onClick={close}
        >
          <ListItemIcon>
            <SupportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Contact support</ListItemText>
        </MenuItem>
        {onToggleV2Pages && <Divider />}
        {onToggleV2Pages && (
          <MenuItem onClick={onToggleV2Pages} sx={{ pr: 1 }}>
            <ListItemIcon>
              <NewPagesIcon fontSize="small" sx={{ color: v2Pages ? '#5ab031' : undefined }} />
            </ListItemIcon>
            <ListItemText primary="New pages" secondary="Preview the redesign" secondaryTypographyProps={{ fontSize: 12 }} />
            <Switch
              edge="end"
              size="small"
              checked={!!v2Pages}
              tabIndex={-1}
              inputProps={{ 'aria-label': 'Use the new pages' }}
              sx={{
                ml: 1,
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#5ab031' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#5ab031' },
              }}
            />
          </MenuItem>
        )}
        {/* Tema: disponibile con le pagine nuove (le classiche restano chiare) */}
        {themePref && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: 14, mb: 0.75 }}>Theme</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={themePref}
              onChange={(_, value) => value && onThemePrefChange(value)}
              aria-label="Theme"
              sx={{ '& .MuiToggleButton-root': { px: 0.75, py: 0.5, textTransform: 'none', fontSize: 12.5, gap: 0.5 }, '& .Mui-selected': { color: '#5ab031 !important' } }}
            >
              <ToggleButton value="light" aria-label="Light theme">
                <LightIcon sx={{ fontSize: 16 }} />
                Light
              </ToggleButton>
              <ToggleButton value="dark" aria-label="Dark theme">
                <DarkIcon sx={{ fontSize: 16 }} />
                Dark
              </ToggleButton>
              <ToggleButton value="oled" aria-label="Dark theme with pure black, for OLED screens">
                <OledIcon sx={{ fontSize: 16 }} />
                OLED
              </ToggleButton>
              <ToggleButton value="system" aria-label="Same as the system">
                <SystemIcon sx={{ fontSize: 16 }} />
                Auto
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        <Divider />
        <MenuItem onClick={() => go('/logout')}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
